// src/components/EventPreviewForm.js
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Container,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import QRCodeStyling from "qr-code-styling";
import { useNavigate, useParams } from "react-router-dom";

// ✅ Firestore
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import CreateSignButton from "./Signs/CreateSignButton";
import QrSharingHelpSheet from "./QrSharingHelpSheet";

/* ---------- small helpers ---------- */

const formatShortDate = (value) => {
  if (!value) return null;

  try {
    // Firestore Timestamp support
    const maybeDate =
      value?.toDate && typeof value.toDate === "function"
        ? value.toDate()
        : value;

    let d;

if (
  typeof maybeDate === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(maybeDate)
) {
  const [year, month, day] = maybeDate.split("-").map(Number);
  d = new Date(year, month - 1, day);
} else if (
  typeof maybeDate === "string" ||
  typeof maybeDate === "number"
) {
  d = new Date(maybeDate);
} else {
  d = maybeDate;
}

    if (!d || isNaN(d.getTime())) return null;

    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const HostFromUrl = (url) => {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const getDocWithTimeout = (docRef, timeoutMs = 8000) =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Event request timed out"));
    }, timeoutMs);

    getDoc(docRef).then(
      (snapshot) => {
        window.clearTimeout(timeoutId);
        resolve(snapshot);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });

/* ---------- component ---------- */

const EventPreviewForm = () => {
  const navigate = useNavigate();
  const { eventId } = useParams(); // ✅ dashboard should route to something like /event/:eventId/qr

  const [eventDetails, setEventDetails] = useState(null);
  const [eventUrl, setEventUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [qrSize, setQrSize] = useState(260);

 const qrRef = useRef(null);
const qrWrapRef = useRef(null);
const qrCodeRef = useRef(null);

/* ---------- responsive QR size ---------- */
useEffect(() => {
  if (!qrWrapRef.current) return;

  const el = qrWrapRef.current;

  const ro = new ResizeObserver((entries) => {
    const w = entries[0]?.contentRect?.width || 260;
    const size = Math.max(
      160,
      Math.min(Math.round(w * 0.78), 360)
    );

    setQrSize(size);
  });

  ro.observe(el);

  return () => ro.disconnect();
}, []);
useEffect(() => {
  if (!eventUrl || !qrRef.current) return;

  qrRef.current.innerHTML = "";

  qrCodeRef.current = new QRCodeStyling({
    width: qrSize,
    height: qrSize,
    type: "svg",
    data: eventUrl,
    margin: 10,

    qrOptions: {
      errorCorrectionLevel: "H",
    },

    dotsOptions: {
      type: "rounded",
      color: "#000000",
    },

    cornersSquareOptions: {
      type: "extra-rounded",
      color: "#000000",
    },

    cornersDotOptions: {
      type: "dot",
      color: "#000000",
    },

    backgroundOptions: {
      color: "#ffffff",
    },
  });

  qrCodeRef.current.append(qrRef.current);

  return () => {
    if (qrRef.current) {
      qrRef.current.innerHTML = "";
    }
  };
}, [eventUrl, qrSize]);

  /* ---------- fetch event (dashboard entry) ---------- */
  useEffect(() => {
    let alive = true;
    let retryTimer = null;

    const url = eventId
      ? `${window.location.origin}/events/${eventId}`
      : "";

    const cacheKey = eventId
      ? `elevate:qrEventData:${eventId}`
      : "";

    const readCachedEvent = () => {
      if (!cacheKey) return null;

      try {
        const cached = sessionStorage.getItem(cacheKey);

        if (!cached) return null;

        const parsed = JSON.parse(cached);

        return parsed && typeof parsed === "object"
          ? parsed
          : null;
      } catch {
        return null;
      }
    };

    const run = async (attempt = 0) => {
      if (!eventId) {
        if (!alive) return;

        setEventDetails(null);
        setEventUrl("");
        setLoading(false);

        return;
      }

      const cachedEvent = readCachedEvent();

      // The dashboard already saves this event before navigating here.
      // Show the QR immediately instead of waiting for Firestore.
      if (cachedEvent) {
        setEventDetails(cachedEvent);
        setEventUrl(url);
        setLoading(false);
      } else if (attempt === 0) {
        setLoading(true);
      }

      try {
        const snap = await getDocWithTimeout(
          doc(db, "events", eventId),
          8000
        );

        if (!alive) return;

        if (!snap.exists()) {
          setEventDetails(null);
          setEventUrl("");
          setLoading(false);

          try {
            sessionStorage.removeItem(cacheKey);
          } catch {}

          return;
        }

        const data = snap.data() || {};
        const details = data.details || {};

        const normalizedEvent = {
          ...cachedEvent,
          ...data,
          eventName:
            details.eventName ||
            data.eventName ||
            cachedEvent?.eventName ||
            "Event",
          eventDate:
            details.eventDate ||
            data.eventDate ||
            cachedEvent?.eventDate ||
            null,
        };

        setEventDetails(normalizedEvent);
        setEventUrl(url);
        setLoading(false);

        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify(normalizedEvent)
          );
        } catch {}
      } catch (e) {
        console.error(
          `Failed to load event (attempt ${attempt + 1}):`,
          e
        );

        if (!alive) return;

        if (attempt < 2) {
          const delay = attempt === 0 ? 1000 : 2500;

          retryTimer = window.setTimeout(() => {
            run(attempt + 1);
          }, delay);

          return;
        }

        // Never leave the QR page stuck forever.
        // The QR itself only requires the event document ID.
        setEventUrl(url);
        setLoading(false);
      }
    };

    run();

    return () => {
      alive = false;

      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [eventId]);

  /* ---------- QR actions ---------- */

  

  const copyLink = async () => {
    if (!eventUrl) return;

    try {
      await navigator.clipboard.writeText(eventUrl);
      alert("Link copied!");
    } catch {
      const ta = document.createElement("textarea");

      ta.value = eventUrl;

      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);

      alert("Link copied!");
    }
  };

  const shareEvent = () => {
    if (!eventUrl) return;

    if (navigator.share) {
      navigator
        .share({
          title: "Event QR Code",
          text: "Scan this QR code to upload photos to our Elevate gallery",
          url: eventUrl,
        })
        .catch(() => {});
    } else {
      alert("Sharing not supported on this browser");
    }
  };

  

  /* ---------- Loading state ---------- */
  if (loading) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 460,
            p: 4,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, #f5f7ff 0%, #eef3ff 100%)",
            boxShadow:
              "0 12px 28px rgba(42,115,232,0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              mb: 0.5,
            }}
          >
            <CircularProgress size={56} thickness={4} />
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 700 }}
          >
            Loading your QR…
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            One moment while we pull your event.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!eventId || !eventUrl) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          textAlign: "center",
          mt: { xs: 3, sm: 5 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 800 }}
        >
          Event not found
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          We couldn’t load that event’s QR.
        </Typography>

        <Button
          onClick={() => navigate("/eventdashboard")}
          sx={{
            mt: 2,
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Back to Event Dashboard
        </Button>
      </Container>
    );
  }

  /* ---------- Main QR UI ---------- */
  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        p: 2,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          mx: "auto",
          maxWidth: "32ch",
          mt: 1,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.2px",
            lineHeight: 1.22,
            fontSize: {
              xs: "1.35rem",
              sm: "1.5rem",
            },
            color: "#000",
          }}
        >
          ✨ Your Event Is Live!
        </Typography>

      <Box
  sx={{
    mt: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.35,
    whiteSpace: "nowrap",
  }}
>
  <Typography
    component="p"
    sx={{
      m: 0,
      fontWeight: 500,
      lineHeight: 1.2,
      color: "text.secondary",
      fontSize: {
        xs: "0.94rem",
        sm: "1.05rem",
      },
    }}
  >
    Start sharing with your guests now
  </Typography>

  <Box sx={{ display: "flex", flex: "0 0 auto" }}>
    <QrSharingHelpSheet />
  </Box>
</Box>

      
      </Box>

      {/* QR card */}
      <Box
        ref={qrWrapRef}
        sx={{
          mt: 2,
          width: "100%",
          maxWidth: 520,
          borderRadius: 4,
          background: "#fff",
          boxShadow:
            "0 12px 32px rgba(0,0,0,0.08)",
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          ref={qrRef}
          sx={{
            p: 1,
            borderRadius: 2,
            background: "#fff",
          }}
        >
          
        </Box>

<Box
  sx={{
    mt: -3,
    width: "90%",
    maxWidth: 420,
    mx: "auto",
    "& button": {
      width: "100%",
    },
  }}
>
  <CreateSignButton
    event={eventDetails}
    eventUrl={eventUrl}
  />
</Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            gap: 3.5,
         mt: 2,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
        
    
            

          <Box sx={{ textAlign: "center" }}>
            <Box
              onClick={copyLink}
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#F3F4F6",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111"
                strokeWidth="1.7"
              >
                <path d="M10 13a5 5 0 0 1 0-7l2-2a5 5 0 0 1 7 7l-1 1" />
                <path d="M14 11a5 5 0 0 1 0 7l-2 2a5 5 0 0 1-7-7l1-1" />
              </svg>
            </Box>

            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: "block",
                fontWeight: 600,
              }}
            >
              Copy Link
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Box
              onClick={shareEvent}
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#F3F4F6",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111"
                strokeWidth="1.7"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.6 13.5l6.8 3.9M15.4 6.6L8.6 10.5" />
              </svg>
            </Box>

            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: "block",
                fontWeight: 600,
              }}
            >
              Share
            </Typography>
          </Box>
                </Box>

       

        
       
      </Box>

      <Button
        onClick={() => navigate("/eventdashboard")}
        sx={{
          mt: 2.5,
          textTransform: "none",
          fontWeight: 700,
          color: "#61646B",
        }}
      >
        Back to Event Dashboard
      </Button>
    </Container>
  );
};

export default EventPreviewForm;